import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { SkillsInput } from "../components/SkillsInput";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, ApiError, type Candidate, type CandidateInput, type Currency } from "../lib/api";
import { DOCUMENT_TYPES, uploadFile, uploadProblem } from "../lib/upload";

type Prefill = Partial<Pick<Candidate, "fullName" | "location" | "currentTitle" | "currentEmployer" | "linkedinUrl" | "source">>;

/**
 * Create or edit a candidate. On create, an optional CV is uploaded and attached, and `initial`
 * can pre-fill fields (such as the source, when adding someone found through a web search).
 */
export function CandidateForm({
  candidate,
  initial,
  submitLabel,
  onSaved,
  onCancel,
}: {
  candidate?: Candidate;
  initial?: Prefill;
  submitLabel: string;
  onSaved: (c: Candidate) => void;
  onCancel?: () => void;
}) {
  const { pending, error, setError, run } = useAction();
  const [existingId, setExistingId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const seed: Prefill = candidate ?? initial ?? {};
  const [f, setF] = useState({
    fullName: seed.fullName ?? "",
    email: candidate?.email ?? "",
    phone: candidate?.phone ?? "",
    location: seed.location ?? "",
    currentTitle: seed.currentTitle ?? "",
    currentEmployer: seed.currentEmployer ?? "",
    salaryExpectation: candidate?.salaryExpectation?.toString() ?? "",
    noticePeriod: candidate?.noticePeriod ?? "",
    linkedinUrl: seed.linkedinUrl ?? "",
    source: seed.source ?? "",
  });
  const [currency, setCurrency] = useState<Currency>(candidate?.currency ?? "GBP");
  const [lawfulBasis, setLawfulBasis] = useState<Candidate["lawfulBasis"]>(candidate?.lawfulBasis ?? "legitimate_interests");
  const [noticeSent, setNoticeSent] = useState(candidate?.privacyNoticeSentOn ?? "");
  const [skills, setSkills] = useState<string[]>(candidate?.skills ?? []);
  const [cv, setCv] = useState<File | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((v) => ({ ...v, [k]: e.target.value }));

  useEffect(() => {
    api
      .candidateSkills()
      .then((r) => setSuggestions(r.skills.map((s) => s.skill)))
      .catch(() => {});
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setExistingId(null);
    const salaryText = f.salaryExpectation.replace(/[£€$,\s]/g, "");
    const salary = salaryText === "" ? null : Number(/k$/i.test(salaryText) ? Number(salaryText.slice(0, -1)) * 1000 : salaryText);
    if (salary !== null && !Number.isInteger(salary)) return setError("Enter the salary expectation as a whole number, e.g. 85000 or 85k.");
    if (cv) {
      const problem = uploadProblem(cv, DOCUMENT_TYPES);
      if (problem) return setError(problem);
    }
    const input: CandidateInput & { fullName: string } = {
      fullName: f.fullName.trim(),
      email: f.email.trim() || null,
      phone: f.phone.trim() || null,
      location: f.location.trim() || null,
      currentTitle: f.currentTitle.trim() || null,
      currentEmployer: f.currentEmployer.trim() || null,
      salaryExpectation: salary,
      currency,
      noticePeriod: f.noticePeriod.trim() || null,
      linkedinUrl: f.linkedinUrl.trim() || null,
      source: f.source.trim() || null,
      skills,
      lawfulBasis,
      privacyNoticeSentOn: noticeSent || null,
    };
    void run(async () => {
      try {
        let saved = candidate ? (await api.updateCandidate(candidate.id, input)).candidate : (await api.createCandidate(input)).candidate;
        if (cv) {
          try {
            saved = (await api.setCandidateCv(saved.id, (await uploadFile(cv)).id)).candidate;
          } catch {
            // The candidate exists now; don't let a retry create a duplicate. Upload from their page.
            sessionStorage.setItem("flash", "Saved, but the CV upload failed. Try uploading it again below.");
          }
        }
        onSaved(saved);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && typeof err.details.existingCandidateId === "string") {
          setExistingId(err.details.existingCandidateId);
        }
        throw err;
      }
    });
  };

  return (
    <form className="form panel" onSubmit={submit}>
      <div className="grid-2">
        <Field label="Full name" required maxLength={200} autoFocus={!candidate} value={f.fullName} onChange={set("fullName")} />
        <Field label="Email" type="email" value={f.email} onChange={set("email")} />
        <Field label="Phone" type="tel" value={f.phone} onChange={set("phone")} />
        <Field label="Location" value={f.location} onChange={set("location")} placeholder="e.g. Manchester" />
        <Field label="Current title" value={f.currentTitle} onChange={set("currentTitle")} />
        <Field label="Current employer" value={f.currentEmployer} onChange={set("currentEmployer")} />
      </div>
      <div className="grid-3">
        <Field label="Salary expectation" inputMode="numeric" value={f.salaryExpectation} onChange={set("salaryExpectation")} placeholder="85000 or 85k" />
        <label className="field">
          <span className="label">Currency</span>
          <select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)}>
            <option value="GBP">GBP £</option>
            <option value="EUR">EUR €</option>
            <option value="USD">USD $</option>
          </select>
        </label>
        <Field label="Notice period" value={f.noticePeriod} onChange={set("noticePeriod")} placeholder="e.g. 1 month" />
      </div>
      <SkillsInput label="Skills" value={skills} onChange={setSkills} suggestions={suggestions} />
      <div className="grid-2">
        <Field label="LinkedIn" type="url" value={f.linkedinUrl} onChange={set("linkedinUrl")} placeholder="https://www.linkedin.com/in/…" />
        <Field label="Source" value={f.source} onChange={set("source")} placeholder="e.g. Referral, LinkedIn, job board" />
      </div>
      <div className="grid-2">
        <label className="field">
          <span className="label">Lawful basis for holding their data</span>
          <select value={lawfulBasis} onChange={(e) => setLawfulBasis(e.target.value as Candidate["lawfulBasis"])}>
            <option value="legitimate_interests">Legitimate interests</option>
            <option value="consent">Consent</option>
          </select>
        </label>
        <Field label="Privacy notice sent on" type="date" value={noticeSent} onChange={(e) => setNoticeSent(e.target.value)} />
      </div>
      {!candidate && (
        <label className="field">
          <span className="label">CV (PDF or Word, up to 10 MB)</span>
          <input
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => setCv(e.target.files?.[0] ?? null)}
          />
        </label>
      )}
      <ErrorNotice error={error} />
      {existingId && (
        <p>
          <Link to={`/candidates/${existingId}`}>Open the existing candidate →</Link>
        </p>
      )}
      <div className="button-row">
        <button className="btn" disabled={pending}>
          {pending ? (cv ? "Uploading…" : "Saving…") : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="link-btn" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
